import { Grid } from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useLocation } from 'react-router-dom';

import SearchAppBar from 'common/components/layouts/AppBar/SearchAppBar';
import Footer from 'common/components/layouts/Footer/Footer';
import SideBar from 'common/components/layouts/SideBar/SideBar';
import { Loader } from 'common/components/Loader/Loader';
import {
	DEFAULT_TENANT_ID,
	PUBLIC_ROUTES,
	oktaLogoutIdleTime,
} from 'common/constants';
import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { LoggedInUserTermsAndPolicyStatus } from 'common/constants/UserManagementConstants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import TermsAndPolicyModal from 'common/pages/TermsAndPolicy/TermsAndPolicyModal';
import { RootState } from 'common/store/AppStore';
import { onLogout } from 'common/utils/authCheck';
import {
	enableBackgroundGradientFlag,
	formatDateTime,
	hasInventoryManagerRole,
} from 'common/utils/utilFunctions';

import useLogoutIdleTimer from 'hooks/useLogoutIdleTimer';

import styles from 'features/common/Common.module.scss';

import {
	useFeatureFlag,
	useOrganizationFeatureFlags,
} from '../../hooks/useFeatureFlags';

import TrialExpirationModal from './TrailExpirationModal';

import Paths from '.';

const Layout = () => {
	const location = useLocation();
	const dispatch = useDispatch();
	const [backgroundColorExist, setBackgroundColorProperty] =
		useState<boolean>(false);
	const currentState = useSelector((state: RootState) => state.common);

	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);
	const isPRCDetailsPage: boolean = useSelector(
		(state: RootState) => state.common.isPRCDetailsPage,
	);
	const { ACCEPTED, DECLINED } = LoggedInUserTermsAndPolicyStatus;

	const isLoggedIn = useSelector(
		(state: RootState) => state.common.isAuthenticated,
	);

	const isLDResponseAvailable = useSelector(
		(state: RootState) => state.common.isLDResponseAvailable,
	);

	const [isResponseAvailable, setIsLDResponseAvailable] = useState<boolean>(
		isLDResponseAvailable,
	);
	const [isTermsAccepted, setIsTermsAccepted] = useState(false);
	const [showTrialExpirationModal, setShowTrialExpirationModal] =
		useState<boolean>(false);

	const hasInvManagerAccess = hasInventoryManagerRole(loggedInUser);

	useEffect(() => {
		if (
			isLoggedIn &&
			!PUBLIC_ROUTES.includes(location?.pathname) &&
			(!loggedInUser?.termsAcknowledgeStatus ||
				loggedInUser?.termsAcknowledgeStatus === DECLINED)
		) {
			setIsTermsAccepted(false);
		} else {
			setIsTermsAccepted(true);
		}
	}, [isLoggedIn, location?.pathname, loggedInUser]);
	const logoutParams = {
		logoutIdelTime: oktaLogoutIdleTime,
		callOnLogout: onLogout,
	};
	const { logoutOnIdle } = useLogoutIdleTimer(logoutParams);
	const isTrialPeriodEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_TRIAL_PERIOD_ENABLED,
	);
	const isTermsAndConditionEnabled = useFeatureFlag(
		FeatureFlagsKeysEnum.IS_TERMS_AND_CONDITION_ENABLED,
	);
	const userData = {
		user: {
			userId: currentState?.loggedInUser?.userId,
			name: `${currentState?.loggedInUser?.firstName} ${currentState?.loggedInUser?.lastName}`,
			email: currentState?.loggedInUser?.email,
			role: currentState?.loggedInUser?.roles?.map(
				(role: any) => role.roleName,
			),
			group: currentState?.loggedInUser?.groups?.[0]?.groupName,
			tenantId:
				localStorageWrapper.getStorageItem('tenantId') || DEFAULT_TENANT_ID,
		},
		organization: {
			name: 'RL-INT',
		},
	};

	useOrganizationFeatureFlags(userData, isLoggedIn, dispatch);

	const isAppDrawerOpen = useSelector(
		(state: RootState) => state.common.isAppDrawerOpen,
	);
	const date = new Date();
	const utcDate = date.toUTCString();

	const termsExpired =
		isLoggedIn &&
		loggedInUser?.termsAcknowledgeStatus === ACCEPTED &&
		loggedInUser?.userEndDate &&
		loggedInUser?.userEndDate < formatDateTime(utcDate);

	useEffect(() => {
		if (isLDResponseAvailable) {
			setIsLDResponseAvailable(true);
		}
	}, [isLDResponseAvailable]);

	useEffect(() => {
		const enableBgColor = enableBackgroundGradientFlag(location?.pathname);
		setBackgroundColorProperty(enableBgColor);
	}, [location?.pathname]);

	useEffect(() => {
		if (isLoggedIn) {
			logoutOnIdle();
		}
	}, [isLoggedIn]);

	useEffect(() => {
		if (!isLoaded) {
			setIsLoaded(true);
		}
	}, [isLoaded]);

	useEffect(() => {
		const enableBgColor = enableBackgroundGradientFlag(location?.pathname);
		setBackgroundColorProperty(enableBgColor);
	}, [location?.pathname]);

	useEffect(() => {
		if (isLoggedIn && termsExpired && isTrialPeriodEnabled) {
			setShowTrialExpirationModal(true);
		} else {
			setShowTrialExpirationModal(false);
		}
	}, [isLoggedIn, termsExpired, isTrialPeriodEnabled]);

	const publicPath = PUBLIC_ROUTES.includes(location?.pathname);
	if (isLoggedIn && !isResponseAvailable) {
		return <Loader show className={styles['layout-progress-bar']} />;
	}
	return (
		<Grid container>
			{isTermsAndConditionEnabled && !isTermsAccepted && (
				<TermsAndPolicyModal />
			)}
			{showTrialExpirationModal && <TrialExpirationModal />}
			{isLoggedIn && !publicPath && isTermsAccepted && !termsExpired ? (
				<>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<SearchAppBar />
					</Grid>
					<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
						<div
							className={
								backgroundColorExist
									? styles['banner-header']
									: styles['banner-header-removed']
							}
						/>
						<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
							<SideBar />
							<div
								className={
									isAppDrawerOpen
										? styles['page-background-expand']
										: styles['page-background-collapse']
								}
							>
								<Paths />
							</div>
						</Grid>

						<Grid
							item
							className={`${
								isAppDrawerOpen
									? styles['footer-text-when-menu-open']
									: styles['footer-text-alignment']
							} ${
								isPRCDetailsPage && hasInvManagerAccess
									? styles['footer-text-margin']
									: ''
							}`}
							xs={12}
							sm={12}
							md={12}
							lg={12}
							xl={12}
							pt={1}
						>
							<Footer />
						</Grid>
					</Grid>
				</>
			) : (
				<Grid item>{isTermsAccepted && !termsExpired && <Paths />}</Grid>
			)}
		</Grid>
	);
};
export default Layout;
